/* eslint-disable @typescript-eslint/no-empty-function */
import { EditableGeometry } from '@components/ui/map/formMap/FormMapContext';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { getFirstPolygon } from '@utils/map/polygon.utils';
import { Map } from 'mapbox-gl';
import { RefObject, useCallback, useEffect, useRef } from 'react';
import { type ControlPosition, IControl, useControl } from 'react-map-gl/mapbox';

import { MapBoxDrawEvent } from '@/types/map.types';

type DrawControlProps = ConstructorParameters<typeof MapboxDraw>[0] & {
  drawRef?: RefObject<MapboxDrawControl | null>;
  position?: ControlPosition;

  onCreate?: (evt: { features: MapBoxDrawEvent[] }) => void;
  onUpdate?: (evt: { features: MapBoxDrawEvent[]; action: string }) => void;

  geometry: EditableGeometry | undefined;
};

export type MapboxDrawControl = MapboxDraw & IControl;

export function DrawControl({
  drawRef,
  position,
  onCreate = () => {},
  onUpdate = () => {},
  geometry,
  ...props
}: DrawControlProps) {
  const mapRef = useRef<Map | null>(null);

  const handleUpdate = useCallback(
    (evt: { features: MapBoxDrawEvent[]; action: string }) => {
      onUpdate(evt);
      mapRef.current?.dragPan.enable();
    },
    [onUpdate],
  );

  const drawInstance = useControl<MapboxDrawControl>(
    () => new MapboxDraw(props) as MapboxDrawControl,
    ({ map }) => {
      mapRef.current = map.getMap() as Map;

      map.on('draw.create', onCreate);
      map.on('draw.update', handleUpdate);

      if (drawRef) {
        drawRef.current = drawInstance;
      }
    },
    ({ map }) => {
      map.off('draw.create', onCreate);
      map.off('draw.update', handleUpdate);
    },
    {
      position: position,
    },
  );

  // trigger after useControl, since useControl allocates drawInstance to map.
  useEffect(() => {
    if (!geometry || geometry.coordinates.length === 0) return;
    drawInstance.deleteAll();
    const featureToAdd = geometry.type === 'Polygon' ? getFirstPolygon(geometry) : geometry;
    const [createdFeature] = drawInstance.add(featureToAdd);
    if (geometry.type === 'Polygon') {
      drawInstance.changeMode('direct_select', {
        featureId: createdFeature,
      });
    } else {
      drawInstance.changeMode('simple_select', {
        featureIds: [createdFeature],
      });
    }
  }, [drawInstance, geometry]);

  return null;
}
